import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Download, Maximize2 } from 'lucide-react';

import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';

import { columnMinWidths, tableRows, toCsv, toTsv } from './tableData';
import './ChatTable.css';

/**
 * Eine Tabelle in einer Chat-Antwort (Markdown), die auch breit lesbar bleibt (Sasse, 06.10.2026).
 *
 * Vorher: `width: 100%` in einem Container mit **ausgeblendeter** Scrollleiste. Passte die Tabelle
 * nicht, quetschte der Browser jede Spalte, bis Wörter einzeln umbrachen; ging es noch weiter,
 * lief sie unsichtbar nach rechts weg — niemand sah, dass dort Spalten kamen. Jetzt:
 *
 * - **Mindestbreite je Spalte nach ihrem Inhalt** (`columnMinWidths`): Eine Tabelle, die passt,
 *   bleibt wie sie war; eine, die nicht passt, rollt seitlich statt zu quetschen.
 * - **Eigene Scrollbox mit sichtbarer Leiste** und einem Verlauf am rechten Rand, solange dort noch
 *   etwas kommt.
 * - **Vollbild, Kopieren (für Excel), CSV** über der Tabelle.
 */
export default function ChatTable({ node, children, ...props }: any) {
    const { t } = useTranslation();
    const rows = React.useMemo(() => tableRows(node), [node]);
    const widths = React.useMemo(() => columnMinWidths(rows), [rows]);
    // Eine Klasse je Tabelle, damit die Spaltenbreiten nur sie treffen.
    const scope = `chat-table-${React.useId().replace(/[^a-zA-Z0-9]/g, '')}`;
    const widthRules = widths
        .map((rem, column) => (rem ? `.${scope} tr > :nth-child(${column + 1}) { min-width: ${rem}rem; }` : ''))
        .join('\n');

    const scrollRef = React.useRef<HTMLDivElement>(null);
    const [moreRight, setMoreRight] = React.useState(false);
    React.useEffect(() => {
        const box = scrollRef.current;
        if (!box) return;
        const update = () => setMoreRight(box.scrollLeft + box.clientWidth < box.scrollWidth - 1);
        update();
        box.addEventListener('scroll', update, { passive: true });
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
        observer?.observe(box);
        return () => {
            box.removeEventListener('scroll', update);
            observer?.disconnect();
        };
    }, []);

    const [fullscreen, setFullscreen] = React.useState(false);
    const [copied, setCopied] = React.useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(toTsv(rows));
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch {
            /* ohne Zwischenablage-Recht: dann eben nicht */
        }
    };
    const download = () => {
        const url = URL.createObjectURL(new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `${t('chatTable.fileName')}.csv`;
        link.click();
        window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
    };

    const table = <table {...props}>{children}</table>;
    const button = 'inline-flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground';

    return (
        <div className={`chat-table ${scope} group/table relative my-2`} data-testid="chat-table">
            {widthRules && <style>{widthRules}</style>}
            <div className="chat-table-tools flex justify-end gap-0.5 opacity-60 transition-opacity focus-within:opacity-100 group-hover/table:opacity-100">
                <button type="button" className={button} onClick={() => setFullscreen(true)} title={t('chatTable.fullscreen')} aria-label={t('chatTable.fullscreen')} data-testid="chat-table-fullscreen">
                    <Maximize2 className="size-3.5" />
                </button>
                <button type="button" className={button} onClick={copy} title={t('chatTable.copy')} aria-label={t('chatTable.copy')} data-testid="chat-table-copy">
                    {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
                </button>
                <button type="button" className={button} onClick={download} title={t('chatTable.download')} aria-label={t('chatTable.download')} data-testid="chat-table-download">
                    <Download className="size-3.5" />
                </button>
            </div>
            <div ref={scrollRef} className="chat-table-scroll overflow-x-auto" data-testid="chat-table-scroll">
                {table}
            </div>
            {moreRight && <div className="chat-table-fade pointer-events-none absolute bottom-0 right-0 top-8 w-8" aria-hidden data-testid="chat-table-more" />}
            <Dialog open={fullscreen} onOpenChange={setFullscreen}>
                <DialogContent className="flex h-[90vh] w-[95vw] max-w-[95vw] flex-col gap-3 sm:max-w-[95vw]">
                    <DialogTitle className="text-sm">{t('chatTable.title')}</DialogTitle>
                    <DialogDescription className="sr-only">{t('chatTable.fullscreen')}</DialogDescription>
                    <div className={`markdown chat-table chat-table-full ${scope} min-h-0 flex-1 overflow-auto`}>{table}</div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
